import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Interface cho thông tin danh mục
 */
export interface Category {
  /** ID duy nhất của danh mục */
  id: number;
  /** Tên danh mục */
  name: string;
  /** Slug URL của danh mục */
  slug: string;
  /** Mô tả danh mục (tùy chọn) */
  description?: string;
  /** Trạng thái hoạt động của danh mục */
  isActive: boolean;
  /** Thời gian tạo */
  createdAt: string;
  /** Thời gian cập nhật cuối */
  updatedAt: string;
}

/**
 * Interface cho tham số lấy danh sách danh mục
 */
export interface ListCategoriesParams {
  /** Trang hiện tại (tùy chọn) */
  page?: number;
  /** Số lượng danh mục mỗi trang (tùy chọn) */
  limit?: number;
  /** Từ khóa tìm kiếm (tùy chọn) */
  search?: string;
  /** Lọc theo trạng thái hoạt động (tùy chọn) */
  isActive?: boolean;
}

/**
 * Interface cho phản hồi danh sách danh mục
 */
export interface ListCategoriesResponse {
  /** Danh sách danh mục */
  data: Category[];
  /** Tổng số danh mục */
  total: number;
  /** Trang hiện tại */
  page: number;
  /** Số lượng danh mục mỗi trang */
  limit: number;
  /** Tổng số trang */
  totalPages: number;
}

/**
 * Interface cho yêu cầu tạo danh mục mới
 */
export interface CreateCategoryRequest {
  /** Tên danh mục */
  name: string;
  /** Slug URL của danh mục (tùy chọn) */
  slug?: string;
  /** Mô tả danh mục (tùy chọn) */
  description?: string;
  /** Trạng thái hoạt động (tùy chọn, mặc định true) */
  isActive?: boolean;
  /** ID danh mục cha (tùy chọn) */
  parentId?: number | null;
}

/**
 * Interface cho yêu cầu cập nhật danh mục
 */
export interface UpdateCategoryRequest {
  /** Tên danh mục (tùy chọn) */
  name?: string;
  /** Slug URL của danh mục (tùy chọn) */
  slug?: string;
  /** Mô tả danh mục (tùy chọn) */
  description?: string;
  /** Trạng thái hoạt động (tùy chọn) */
  isActive?: boolean;
  /** ID danh mục cha (tùy chọn) */
  parentId?: number | null;
}

/**
 * Service xử lý các chức năng quản lý danh mục
 * Bao gồm tạo, đọc, cập nhật, xóa danh mục
 */
export class CategoriesService {
  /** URL cơ sở của API */
  private static baseUrl = getApiBase();

  /**
   * Lấy headers xác thực cho các request API
   * @returns Record<string, string> - Headers bao gồm Content-Type và Authorization
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  // ================== PUBLIC CATEGORIES ==================
  
  /**
   * Lấy danh sách danh mục public (không cần đăng nhập)
   * @returns Promise<Category[]> - Danh sách danh mục active
   * @throws Error - Khi không thể tải danh sách danh mục
   */
  static async getPublicCategories(): Promise<Category[]> {
    const response = await fetch(`${this.baseUrl}/public/categories`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách danh mục';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  // ================== CATEGORIES (Admin) ==================
  
  /**
   * Lấy danh sách danh mục với phân trang và tìm kiếm
   * @param params - Tham số lọc và phân trang
   * @returns Promise<ListCategoriesResponse> - Danh sách danh mục với thông tin phân trang
   * @throws Error - Khi không thể tải danh sách danh mục
   */
  static async listCategories(params: ListCategoriesParams = {}): Promise<ListCategoriesResponse> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.search) query.append('search', params.search);
    if (typeof params.isActive === 'boolean') query.append('isActive', String(params.isActive));
    
    const response = await apiClient.get(`/categories?${query.toString()}`.replace(/\?$/, ''));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách danh mục';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Tạo danh mục mới
   * @param data - Thông tin danh mục cần tạo
   * @returns Promise<Category> - Thông tin danh mục vừa tạo
   * @throws Error - Khi tạo danh mục thất bại
   */
  static async createCategory(data: CreateCategoryRequest): Promise<Category> {
    const response = await apiClient.post('/categories', data);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Tạo danh mục thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Cập nhật thông tin danh mục
   * @param id - ID của danh mục cần cập nhật
   * @param data - Thông tin danh mục cần cập nhật
   * @returns Promise<Category> - Thông tin danh mục sau khi cập nhật
   * @throws Error - Khi cập nhật danh mục thất bại
   */
  static async updateCategory(id: number, data: UpdateCategoryRequest): Promise<Category> {
    const response = await apiClient.patch(`/categories/${id}`, data);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật danh mục thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Xóa danh mục
   * @param id - ID của danh mục cần xóa
   * @returns Promise<void>
   * @throws Error - Khi xóa danh mục thất bại
   */
  static async deleteCategory(id: number): Promise<void> {
    const response = await apiClient.delete(`/categories/${id}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Xóa danh mục thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  }
}
